/**
 * audio.ts — Web Audio API sound synthesis module (Phase 43).
 *
 * Provides all 6 game sounds synthesized via OscillatorNode + GainNode chains.
 * AudioContext is created lazily on first playSound() call — never at module load
 * (per SOUND-01: browsers block AudioContext before a user gesture).
 *
 * Each sound creates its own GainNode instance (per SOUND-03).
 * Mute state is cached as a module boolean and persisted to sessionStorage key
 * 'cnf-game-muted'. The cached boolean is read on every isMuted() call — not
 * sessionStorage — to avoid per-frame storage reads at 60fps.
 *
 * Exports: playSound, setMuted, isMuted, ensureAudioUnlocked, SoundName
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SoundName =
  | 'dialogueTick'
  | 'letterDestroy'
  | 'wordCleared'
  | 'playerHit'
  | 'gameStart'
  | 'exitSound';

// ---------------------------------------------------------------------------
// Lazy AudioContext singleton (SOUND-01)
// CRITICAL: getCtx() is NEVER called at module scope — only inside play* functions.
// ---------------------------------------------------------------------------

let _ctx: AudioContext | null = null;

/**
 * Resume the AudioContext if it exists and is suspended.
 * Called from user gesture handlers AND from visibilitychange so the context
 * is running before any audio is scheduled.
 */
function resumeIfSuspended(): void {
  if (_ctx && _ctx.state === 'suspended') {
    _ctx.resume().catch(() => undefined);
  }
}

/**
 * visibilitychange listener — Chrome suspends AudioContext when the tab loses
 * focus. Resuming here means the context is already running by the time the
 * user presses a key after switching back. Without this, resume() and
 * playNote() race in the same synchronous tick: ctx.currentTime is still
 * frozen when the oscillator start time is computed, so the note is dropped.
 */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      resumeIfSuspended();
    }
  });
}

/**
 * Call this from the user gesture that starts the game (e.g. the Enter press
 * in CommandPalette). Creates the AudioContext while still inside the gesture
 * stack so Chrome starts it in 'running' state — never suspended.
 * Must be called BEFORE activate() triggers the React re-render that runs
 * playSound('gameStart') in useEffect.
 */
export function primeAudio(): void {
  const ctx = getCtx(); // create (or no-op if already exists) inside the current gesture
  console.log('[audio] primeAudio — state:', ctx.state);
}

/**
 * Call this from any user gesture handler (keydown, click) to resume a
 * suspended context (e.g. after a tab-blur re-suspension).
 */
export function ensureAudioUnlocked(): void {
  resumeIfSuspended();
}

function getCtx(): AudioContext {
  const isNew = !_ctx;
  if (!_ctx) _ctx = new AudioContext();
  if (isNew) console.log('[audio] AudioContext created — state:', _ctx.state);
  resumeIfSuspended();
  return _ctx;
}

// ---------------------------------------------------------------------------
// Mute state — cached boolean + lazy sessionStorage init (D-mute)
// ---------------------------------------------------------------------------

let _muted: boolean = false;
let _mutedInitialized = false;

function ensureMuteInit(): void {
  if (_mutedInitialized) return;
  _mutedInitialized = true;
  _muted = sessionStorage.getItem('cnf-game-muted') === '1';
}

export function isMuted(): boolean {
  ensureMuteInit();
  return _muted;
}

export function setMuted(muted: boolean): void {
  _mutedInitialized = true;
  _muted = muted;
  sessionStorage.setItem('cnf-game-muted', muted ? '1' : '0');
}

// ---------------------------------------------------------------------------
// Note frequency map (locked per 43-CONTEXT.md)
// ---------------------------------------------------------------------------

const FREQ: Record<string, number> = {
  C3: 130.81,
  G3: 196,
  A3: 220,
  C4: 261.63,
  E4: 329.63,
  G4: 392,
  A4: 440,
  C5: 523.25,
};

// ---------------------------------------------------------------------------
// Helper — play a single oscillator note (SOUND-03: each call creates its own GainNode)
// ---------------------------------------------------------------------------

function playNote(
  freq: number,
  durationSec: number,
  type: OscillatorType = 'sine',
  gain = 0.3,
  startOffset = 0
): void {
  const ctx = getCtx();
  // Notes scheduled at a frozen ctx.currentTime (while context is resuming) will
  // fire immediately once the context becomes running — do NOT skip them.
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.connect(g);
  g.connect(ctx.destination);
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime + startOffset);
  g.gain.setValueAtTime(gain, ctx.currentTime + startOffset);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startOffset + durationSec);
  osc.start(ctx.currentTime + startOffset);
  osc.stop(ctx.currentTime + startOffset + durationSec + 0.01);
  osc.onended = () => {
    osc.disconnect();
    g.disconnect();
  };
}

// ---------------------------------------------------------------------------
// Sound implementations (per SOUND-02 locked specs in 43-CONTEXT.md)
// ---------------------------------------------------------------------------

/**
 * dialogueTick — ~800Hz sine, 30ms, ±15% pitch jitter per tick.
 * Fires for each character emitted by the dialogue typewriter.
 */
function playDialogueTick(): void {
  playNote(800 * (0.85 + Math.random() * 0.3), 0.03, 'sine', 0.15);
}

/**
 * letterDestroy — 400→200Hz sweep, 80ms, triangle wave.
 * Fires per correctly-typed letter (the "zap" on each keystroke).
 */
function playLetterDestroy(): void {
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.connect(g);
  g.connect(ctx.destination);
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(400, ctx.currentTime);
  osc.frequency.linearRampToValueAtTime(200, ctx.currentTime + 0.08);
  g.gain.setValueAtTime(0.25, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.09);
  osc.onended = () => {
    osc.disconnect();
    g.disconnect();
  };
}

/**
 * wordCleared — C4-E4-G4 arpeggio, 80ms each, sine.
 * Fires when a full word is destroyed.
 */
function playWordCleared(): void {
  playNote(FREQ.C4, 0.08, 'sine', 0.3, 0);
  playNote(FREQ.E4, 0.08, 'sine', 0.3, 0.08);
  playNote(FREQ.G4, 0.08, 'sine', 0.3, 0.16);
}

/**
 * playerHit — ~100Hz square wave (150ms) + noise burst (150ms).
 * Fires when a word enemy reaches the cursor (player takes damage).
 */
function playPlayerHit(): void {
  // Square wave low thud
  playNote(100, 0.15, 'square', 0.25);

  // Noise burst via AudioBufferSourceNode
  const ctx = getCtx();
  const bufferSize = Math.ceil(ctx.sampleRate * 0.15);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.15, ctx.currentTime);
  noise.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noise.start(ctx.currentTime);
  noise.stop(ctx.currentTime + 0.15);
  noise.onended = () => {
    noise.disconnect();
    noiseGain.disconnect();
  };
}

/**
 * gameStart — C4-E4-G4-A4-C5 major pentatonic ascending, 80ms each, sine.
 * Fires when the game activates (first user gesture — AudioContext created here).
 */
function playGameStart(): void {
  playNote(FREQ.C4, 0.08, 'sine', 0.3, 0);
  playNote(FREQ.E4, 0.08, 'sine', 0.3, 0.08);
  playNote(FREQ.G4, 0.08, 'sine', 0.3, 0.16);
  playNote(FREQ.A4, 0.08, 'sine', 0.3, 0.24);
  playNote(FREQ.C5, 0.08, 'sine', 0.3, 0.32);
}

/**
 * exitSound — C4-A3-G3 descending 3-note, 80ms each, sine with soft decay.
 * Fires when the player exits the game.
 */
function playExitSound(): void {
  playNote(FREQ.C4, 0.08, 'sine', 0.25, 0);
  playNote(FREQ.A3, 0.08, 'sine', 0.25, 0.08);
  playNote(FREQ.G3, 0.08, 'sine', 0.25, 0.16);
}

// ---------------------------------------------------------------------------
// Main dispatcher
// ---------------------------------------------------------------------------

export function playSound(name: SoundName): void {
  ensureMuteInit();
  console.log('[audio] playSound:', name, '— ctx state:', _ctx ? _ctx.state : 'no ctx', '— muted:', _muted);
  if (_muted) return;
  switch (name) {
    case 'dialogueTick':
      playDialogueTick();
      break;
    case 'letterDestroy':
      playLetterDestroy();
      break;
    case 'wordCleared':
      playWordCleared();
      break;
    case 'playerHit':
      playPlayerHit();
      break;
    case 'gameStart':
      playGameStart();
      break;
    case 'exitSound':
      playExitSound();
      break;
  }
}
